/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useMemo, useState } from 'react';

import { Hcp } from '@/core/graphql/generated/types';
import { isOfTypeHcp } from '@/utils/hcp/isOfTypeHcp';
import { useHcpUrlParams } from '@/utils/hcp/useHcpUrlParams';

import {
	GetHcpByIdQuery,
	useGetHcpByIdLazyQuery
} from '../_graphql/query/GetHCPById.hook';

const DEFAULT_AFFILIATIONS_PAGE_SIZE = 4;

type UseHcpDataOptions = {
	suppressErrors?: boolean;
	affiliationsPageSize?: number;
	enabled?: boolean;
};

export const useHcpData = ({
	suppressErrors = false,
	affiliationsPageSize = DEFAULT_AFFILIATIONS_PAGE_SIZE,
	enabled = true
}: UseHcpDataOptions = {}) => {
	const { id } = useHcpUrlParams(suppressErrors);
	const [getHcpById, { data, loading: queryLoading }] = useGetHcpByIdLazyQuery({
		fetchPolicy: 'cache-and-network'
	});
	const [sessionData, setSessionData] = useState<GetHcpByIdQuery | undefined>();

	useEffect(() => {
		if (id && enabled) {
			setSessionData(undefined);
			getHcpById({
				variables: {
					vid: id,
					affiliationsPageSize
				}
			});
		} else if (!enabled) {
			setSessionData(undefined);
		}
	}, [getHcpById, id, affiliationsPageSize, enabled]);

	useEffect(() => {
		if (enabled && data) setSessionData(data);
	}, [enabled, data]);

	const loading = enabled && (queryLoading || (!!id && !sessionData));

	const hcp = useMemo(() => {
		return sessionData?.hcp;
	}, [sessionData]);

	if (
		!loading &&
		hcp &&
		'__typename' in hcp &&
		hcp.__typename === 'ResourceNotFound'
	) {
		return {
			hcp: null,
			loading,
			isInvalidHcp: true
		};
	}

	return {
		hcp: isOfTypeHcp(hcp) ? (hcp as Hcp) : null,
		loading,
		isInvalidHcp: false
	};
};
